// prettier-ignore
import 'react-quill/dist/quill.snow.css';
// prettier-ignore
import './styles/convenience.css';
// THREE.js BVH extensions - must be imported once at app startup
import 'src/utils/three-extensions';

import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { AppShell } from '@mantine/core';
import { Box } from 'src/design/atoms/Box';
import { Loader } from 'src/design/atoms/Loader';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Dialogs } from 'src/app/dialogs/Dialogs';
import { NavigationHeader } from 'src/app/navigation/NavigationHeader';
import { Sidebar } from 'src/app/navigation/Sidebar';
import { NotFound } from 'src/app/navigation/NotFound';
import { useAuth } from 'src/hooks/useAuth';
import { useHomeRoute } from 'src/hooks/useHomeRoute';
import { AssumedUserBanner } from 'src/design/molecules/AssumedUserBanner';
import { DialogsProvider } from 'src/hooks/useDialogs';
import { ChatProvider } from 'src/hooks/useChat';
import { JobsProvider } from 'src/hooks/useJobs';
import { OrganizationProvider } from 'src/hooks/useOrganization';
import { ChatDrawer } from 'src/app/chat/ChatDrawer';
import { getErrorMessage, logger } from 'src/utils';
import { signOut } from 'src/auth/signOut';
import { getStatusCode } from 'src/api/queries/errorClassification';
import { AccountUnavailable } from 'src/app/authorization/AccountUnavailable';
import { drawerWidth, JOB_BAR_HEIGHT, NAV_ROW_HEIGHT } from './config';
import { AccountProfile } from 'src/routes/AccountProfile';
import { Permissions } from 'src/routes/AccountProfile/Permissions';
import { Activity } from 'src/routes/Activity';
import { Changes } from 'src/routes/Changes';
import { RFIs } from 'src/routes/RFIs';
import { Inspections } from 'src/routes/Inspections';
import { Analytics } from 'src/routes/Analytics';
import { Safety } from 'src/routes/Safety';
import { CTC } from 'src/routes/CTC';
import { ViewCTCJob } from 'src/routes/CTC/ViewCTCJob';
import { Jobs } from 'src/routes/Jobs';
import { JobRouter } from 'src/routes/Jobs/JobRouter';
import { Login } from 'src/routes/Login';
import { Logout } from 'src/routes/Logout';
import { DailySubmissionReport } from 'src/routes/Reports/DailySubmissionReport';
import { TimesheetAuditReport } from 'src/routes/Reports/TimesheetAuditReport';
import { TimesheetPayrollReport } from 'src/routes/Reports/TimesheetPayrollReport';
import { Classifications } from 'src/routes/Settings/Classifications';
import { CostCodes } from 'src/routes/Settings/CostCodes';
import { PayCodes } from 'src/routes/Settings/PayCodes';
import { Rates } from 'src/routes/Settings/Rates';
import { Time } from 'src/routes/Time';
import { RoleBasedRoute } from 'src/app/authorization/RoleBasedRoute';
import { useMenu } from './hooks/useMenu';
import { ExternalAttachmentRedirect } from './routes/External/Attachment/ExternalAttachmentRedirect';
import { CombinedTimesheetReview } from './routes/External/Timesheet/CombinedTimesheetReview';
import { TimesheetReview } from './routes/External/Timesheet/TimesheetReview';
import { Privacy } from './routes/Legal/Privacy';
import { SmsTerms } from './routes/Legal/SmsTerms';
import { WbsProvider } from './routes/Jobs/WBS/contexts/WbsContext';
import { Manhours } from './routes/Manhours';
import { Operations } from './routes/Operations';
import { Daily } from './routes/Operations/Daily';
import { Reports } from './routes/Reports';
import { ServiceWork } from './routes/ServiceWork';
import { SmsConsent } from './routes/SmsConsent';
import { SettingsChanges } from './routes/Settings/Changes';
import { CreateChangeTemplate } from './routes/Settings/Changes/CreateTemplate';
import { UpdateChangeTemplate } from './routes/Settings/Changes/UpdateTemplate';
import { Contacts } from './routes/Settings/Contacts';
import { Customers } from './routes/Settings/Customers';
import { Documents } from './routes/Settings/Documents';
import { CreateDocumentTemplate } from './routes/Settings/Documents/CreateTemplate';
import { Healthcheck } from './routes/Settings/Healthcheck';
import { Milestones } from './routes/Settings/Milestones';
import { CreateMilestoneTemplate } from './routes/Settings/Milestones/CreateMilestoneTemplate';
import { Offices } from './routes/Settings/Offices';
import { CreateOffice } from './routes/Settings/Offices/Create';
import { UpdateOffice } from './routes/Settings/Offices/Update';
import { ChangeTypes } from './routes/Settings/ChangeTypes';
import { CreateChangeType } from './routes/Settings/ChangeTypes/Create';
import { UpdateChangeType } from './routes/Settings/ChangeTypes/Update';
import { Roles } from './routes/Settings/Roles';
import { CreateRole } from './routes/Settings/Roles/Create';
import { UpdateRole } from './routes/Settings/Roles/Update';
import { AIAssistantAudit } from './routes/Settings/AIAssistant';
import { AIAssistantUserDetail } from './routes/Settings/AIAssistant/UserDetail';
import { AzureGroupSync } from './routes/Settings/AzureGroupSync';
import { BackgroundJobs } from './routes/Settings/BackgroundJobs';
import { Applogs } from './routes/Settings/Applogs';
import { ApiConnections } from './routes/Settings/ApiConnections';
import { Proact } from './routes/Settings/Proact';
import { ScimIntegrations } from './routes/Settings/ScimIntegrations';
import { UserHistory } from './routes/Settings/UserHistory';
import { Users } from './routes/Settings/Users';
import { CreateUser } from './routes/Settings/Users/Create';
import { UpdateUser } from './routes/Settings/Users/Update';
import { Workflows } from './routes/Settings/Workflows';
import { EditWorkflowTemplate } from './routes/Settings/Workflows/EditWorkflowTemplate';

const REDIRECT_KEY = '@Firetrol:redirect';

interface AppRoutesProps {
  initialLocation: string;
}
// Exported for testing — no other module should import this directly,
// App remains the default export.
export const AppRoutes: React.FC<AppRoutesProps> = ({ initialLocation }) => {
  const navigate = useNavigate();
  const { drawerOpen, toggleDrawer, isMobile } = useMenu();
  const {
    loadUserFromCache,
    checkFeatureAllowed,
    user,
    assumedUser,
    stopAssuming,
  } = useAuth();
  const assumedUserName = assumedUser
    ? [assumedUser.firstName, assumedUser.lastName].filter(Boolean).join(' ')
    : undefined;
  const hasJobs = checkFeatureAllowed(['jobs']);
  const headerHeight =
    isMobile && hasJobs ? NAV_ROW_HEIGHT + JOB_BAR_HEIGHT : NAV_ROW_HEIGHT;
  const [loading, setLoading] = React.useState(true);
  const [refusedMessage, setRefusedMessage] = React.useState<string | null>(
    null,
  );
  const { instance } = useMsal();

  // The key only carries a signed-out deep link through sign-in. Read it once
  // here, before the user loads, so a later /login visit cannot reuse it.
  const [savedRedirect] = React.useState(() =>
    localStorage.getItem(REDIRECT_KEY),
  );
  React.useEffect(() => localStorage.removeItem(REDIRECT_KEY), []);

  // A 403 means the token is valid but the API refuses this account (e.g. it
  // is archived). Clearing the MSAL cache and returning to /login would sign
  // the user straight back in via SSO and loop, so the refusal is shown here.
  const setUserDetails = async () => {
    try {
      await loadUserFromCache();
      setLoading(false);
    } catch (err: any) {
      if (getStatusCode(err) === 403) {
        setRefusedMessage(
          getErrorMessage(err, 'Your account does not have access.'),
        );
        setLoading(false);
        return;
      }
      await instance.clearCache();
      navigate('/login');
    }
  };

  const signOutRefusedAccount = () => {
    signOut(
      instance,
      instance.getActiveAccount() ?? instance.getAllAccounts()[0],
    );
  };

  React.useEffect(() => {
    setUserDetails();
  }, []);

  React.useEffect(() => {
    const existingScript = document.getElementById('google-maps-script');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyCO_X-wfv1l__3plRnHPIymbJ2MkB6xzBU&libraries=places';
      script.id = 'google-maps-script';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const redirectRoute = useHomeRoute();

  const useCTC = React.useMemo(() => {
    const hasCTC = checkFeatureAllowed(['modules_all', 'ctc']);
    return hasCTC;
  }, [user]);

  if (loading) {
    return (
      <Box style={{ width: '100%', textAlign: 'center' }}>
        <Loader style={{ margin: 'auto', marginTop: '48vh' }} />
        <h3>Loading Insites...</h3>
      </Box>
    );
  }

  if (refusedMessage) {
    return (
      <AccountUnavailable
        message={refusedMessage}
        onSignOut={signOutRefusedAccount}
      />
    );
  }

  // Backend always returns false while Sms:Enabled=false, so this gate is
  // safe to leave unconditional on the frontend.
  if (user?.requiresSmsConsentPrompt) {
    return <SmsConsent onComplete={loadUserFromCache} />;
  }

  return (
    <JobsProvider>
      <OrganizationProvider>
        <WbsProvider>
          <DialogsProvider>
            <ChatProvider>
              <Dialogs />
              <AssumedUserBanner
                assumedUserName={assumedUserName}
                onStopAssuming={stopAssuming}
              />
              <ChatDrawer />
              <AppShell
                header={{ height: headerHeight }}
                navbar={{
                  width: drawerWidth,
                  breakpoint: 'lg',
                  collapsed: { desktop: !drawerOpen, mobile: !drawerOpen },
                }}
                padding="md"
              >
                <AppShell.Header>
                  <NavigationHeader
                    open={drawerOpen}
                    handleDrawerToggle={toggleDrawer}
                  />
                </AppShell.Header>

                <AppShell.Navbar>
                  <Sidebar />
                </AppShell.Navbar>

                <AppShell.Main>
                  <ErrorBoundary
                    key={location.pathname}
                    fallback={
                      <div>Error loading content for {location.pathname}</div>
                    }
                  >
                    {/* row 1 */}
                    <Routes>
                      <Route
                        path="/account/profile"
                        element={<AccountProfile />}
                      />
                      <Route
                        path="/account/permissions"
                        element={<Permissions />}
                      />
                      <Route
                        path="/jobs/:id/*"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['jobs', 'modules_all']}
                            render={() => <JobRouter />}
                          />
                        }
                      />
                      <Route
                        path="/jobs"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['jobs', 'modules_all']}
                            render={() => <Jobs />}
                          />
                        }
                      />
                      <Route
                        path="/analytics"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['analytics', 'modules_all']}
                            render={() => <Analytics />}
                          />
                        }
                      />
                      <Route
                        path="/safety"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['safety', 'modules_all']}
                            render={() => <Safety />}
                          />
                        }
                      />
                      <Route
                        path="/time"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['time', 'modules_all']}
                            render={() => <Time />}
                          />
                        }
                      />
                      <Route
                        path="/activity"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['activity_feed', 'modules_all']}
                            render={() => <Activity />}
                          />
                        }
                      />
                      <Route
                        path="/changes"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['changes', 'modules_all']}
                            render={() => <Changes />}
                          />
                        }
                      />
                      <Route
                        path="/rfis"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['rfis', 'modules_all']}
                            render={() => <RFIs />}
                          />
                        }
                      />
                      <Route
                        path="/inspections"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['inspections', 'modules_all']}
                            render={() => <Inspections />}
                          />
                        }
                      />
                      <Route
                        path="/reports"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['reports', 'modules_all']}
                            render={() => <Reports />}
                          />
                        }
                      />
                      <Route
                        path="/reports/timesheet-payroll"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['reports_timesheet_payroll']}
                            render={() => <TimesheetPayrollReport />}
                          />
                        }
                      />
                      <Route
                        path="/reports/timesheet-audit"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['reports_timesheet_audit']}
                            render={() => <TimesheetAuditReport />}
                          />
                        }
                      />
                      <Route
                        path="/reports/daily-submission"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['jobs_dailys', 'modules_all']}
                            render={() => <DailySubmissionReport />}
                          />
                        }
                      />
                      <Route
                        path="/manhours"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['manhours', 'modules_all']}
                            render={() => <Manhours />}
                          />
                        }
                      />
                      <Route
                        path="/service-work"
                        element={
                          <RoleBasedRoute
                            requiredScopes={[
                              'service_work',
                              'service_work_all',
                              'modules_all',
                            ]}
                            render={() => <ServiceWork />}
                          />
                        }
                      />
                      <Route
                        path="/operations"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['operations', 'modules_all']}
                            render={() => <Operations />}
                          />
                        }
                      />
                      <Route
                        path="/operations/daily"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['operations', 'modules_all']}
                            render={() => <Daily />}
                          />
                        }
                      />
                      <Route
                        path="/ctc"
                        element={
                          <>
                            {useCTC ? (
                              <RoleBasedRoute
                                requiredScopes={['modules_all', 'ctc']}
                                render={() => <CTC />}
                              />
                            ) : (
                              <Navigate replace to={redirectRoute} />
                            )}
                          </>
                        }
                      />
                      <Route
                        path="/ctc/view/:id"
                        element={
                          <RoleBasedRoute
                            requiredScopes={['ctc', 'modules_all']}
                            render={() => <ViewCTCJob />}
                          />
                        }
                      />

                      <Route
                        path="/settings"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['settings_all', 'settings_user']}
                              render={() => <Navigate to="/settings/users" />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/users"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['settings_all', 'settings_user']}
                              render={() => <Users />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/users/create"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['settings_all', 'settings_user']}
                              render={() => <CreateUser />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/users/edit/:id"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['settings_all', 'settings_user']}
                              render={() => <UpdateUser />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/rates"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_rates',
                              ]}
                              render={() => <Rates />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/pay-codes"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_pay_codes',
                              ]}
                              render={() => <PayCodes />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/classifications"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_classifications',
                              ]}
                              render={() => <Classifications />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/contacts"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_contacts',
                              ]}
                              render={() => <Contacts />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/change-types"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_changes',
                              ]}
                              render={() => <ChangeTypes />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/change-types/create"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_changes',
                              ]}
                              render={() => <CreateChangeType />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/change-types/edit/:id"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_changes',
                              ]}
                              render={() => <UpdateChangeType />}
                            />
                          </>
                        }
                      />

                      <Route
                        path="/settings/roles"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_roles',
                              ]}
                              render={() => <Roles />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/roles/create"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_roles',
                              ]}
                              render={() => <CreateRole />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/roles/edit/:id"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_roles',
                              ]}
                              render={() => <UpdateRole />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/offices"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_offices',
                              ]}
                              render={() => <Offices />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/offices/create"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_offices',
                              ]}
                              render={() => <CreateOffice />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/offices/edit/:id"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_offices',
                              ]}
                              render={() => <UpdateOffice />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/cost-codes"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_cost_codes',
                              ]}
                              render={() => <CostCodes />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/customers"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_customers',
                              ]}
                              render={() => <Customers />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/workflows"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_workflows',
                              ]}
                              render={() => <Workflows />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/workflows/:templateId"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_workflows',
                              ]}
                              render={() => <EditWorkflowTemplate />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/documents"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_documents',
                              ]}
                              render={() => <Documents />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/documents/templates/:id"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_documents',
                              ]}
                              render={() => <CreateDocumentTemplate />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/user_history"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_login_history',
                              ]}
                              render={() => <UserHistory />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/milestones"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_milestones',
                              ]}
                              render={() => <Milestones />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/changes"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_changes',
                              ]}
                              render={() => <SettingsChanges />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/changes/template/create"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_changes',
                              ]}
                              render={() => <CreateChangeTemplate />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/changes/template/edit/:id"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_changes',
                              ]}
                              render={() => <UpdateChangeTemplate />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="settings/milestones/templates/:id"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={[
                                'settings_all',
                                'settings_milestones',
                              ]}
                              render={() => <CreateMilestoneTemplate />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/api-connections"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['sys_admin']}
                              render={() => <ApiConnections />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/proact"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['sys_admin']}
                              render={() => <Proact />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/scim-integrations"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['sys_admin']}
                              render={() => <ScimIntegrations />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/azure-group-sync"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['sys_admin']}
                              render={() => <AzureGroupSync />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/app-logs"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['sys_admin']}
                              render={() => <Applogs />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/ai-assistant"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['sys_admin']}
                              render={() => <AIAssistantAudit />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/ai-assistant/users/:userId"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['sys_admin']}
                              render={() => <AIAssistantUserDetail />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/background-jobs"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['sys_admin']}
                              render={() => <BackgroundJobs />}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/settings/healthcheck"
                        element={
                          <>
                            <RoleBasedRoute
                              requiredScopes={['sys_admin']}
                              render={() => <Healthcheck />}
                            />
                          </>
                        }
                      />
                      <Route path="/logout" element={<Logout />} />
                      <Route
                        path="/"
                        element={<Navigate replace to={redirectRoute} />}
                      />
                      <Route
                        path="/login"
                        element={
                          <Navigate
                            replace
                            to={savedRedirect || redirectRoute}
                          />
                        }
                      />
                      <Route
                        path="/external/timesheet/:timesheetId"
                        element={<TimesheetReview />}
                      />
                      <Route
                        path="/external/timesheet/batch/:token"
                        element={<CombinedTimesheetReview />}
                      />
                      <Route
                        path="/external/:productType/:parentId/attachment/:attachmentId"
                        element={<ExternalAttachmentRedirect />}
                      />
                      <Route path="/legal/sms-terms" element={<SmsTerms />} />
                      <Route path="/legal/sms" element={<SmsTerms />} />
                      <Route path="/legal/privacy" element={<Privacy />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </ErrorBoundary>
                </AppShell.Main>
              </AppShell>
            </ChatProvider>
          </DialogsProvider>
        </WbsProvider>
      </OrganizationProvider>
    </JobsProvider>
  );
};

interface AuthRoutesProps {
  initialLocation?: string | null;
}
const AuthRoutes: React.FC<AuthRoutesProps> = ({ initialLocation }) => {
  const location = useLocation();

  React.useEffect(() => {
    // Save the current location so login can return the user to it, but never
    // for public /external/ links — they immediately redirect away, and a stale
    // saved value would strand the user on a blank page after a later login.
    if (
      location.pathname !== '/login' &&
      !location.pathname.startsWith('/external/')
    ) {
      localStorage.setItem(
        REDIRECT_KEY,
        `${location.pathname}${location.search || ''}`,
      );
    }
  }, [location]);

  return (
    <Routes>
      <Route
        path="/external/timesheet/:timesheetId"
        element={<TimesheetReview />}
      />
      <Route
        path="/external/timesheet/batch/:token"
        element={<CombinedTimesheetReview />}
      />
      <Route
        path="/external/:productType/:parentId/attachment/:attachmentId"
        element={<ExternalAttachmentRedirect />}
      />
      <Route path="/legal/sms-terms" element={<SmsTerms />} />
      <Route path="/legal/sms" element={<SmsTerms />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route
        path="/login"
        element={<Login initialLocation={initialLocation} />}
      />
      <Route path="/logout" element={<Navigate replace to="/login" />} />
      <Route path="/*" element={<Navigate replace to="/login" />} />
    </Routes>
  );
};

const REFRESH_THRESHOLD = 300; // 5 minutes in seconds
const TOKEN_CHECK_INTERVAL = 60000; // 1 minute in milliseconds

function App() {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const [loading, setLoading] = React.useState(true);
  const { instance, inProgress } = useMsal();

  React.useEffect(() => {
    const initializeApp = async () => {
      await instance.initialize();
    };
    initializeApp();
  }, []);

  React.useEffect(() => {
    const updateRedirect = async () => {
      try {
        await instance.initialize();

        const activeInteraction =
          instance.getActiveAccount() ||
          instance.getAllAccounts().length > 0 ||
          inProgress === 'login';

        if (!activeInteraction) {
          // Only save the redirect if we're not already on the login page and
          // not on a public /external/ link (which redirects away immediately).
          if (
            location.pathname !== '/login' &&
            !location.pathname.startsWith('/external/')
          ) {
            localStorage.setItem(
              REDIRECT_KEY,
              `${location.pathname}${location.search || ''}`,
            );
          }
        }
      } catch (error) {
        logger.error('Error during app initialization', { err: error });
      } finally {
        setLoading(false);
      }
    };

    updateRedirect();
  }, [location.pathname, location.search, instance]);

  if (
    loading ||
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect
  ) {
    return (
      <Box style={{ width: '100%', textAlign: 'center' }}>
        <Loader style={{ margin: 'auto', marginTop: '48vh' }} />
        <h3>Loading Insites...</h3>
      </Box>
    );
  }

  return !isAuthenticated ? (
    <AuthRoutes initialLocation={location.pathname} />
  ) : (
    <AppRoutes initialLocation={location.pathname} />
  );
}

export default App;
